import { Box, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

/**
 * En-tete de page (maquette « En-tête de page ») : surtitre, titre, sous-titre,
 * actions.
 *
 * Le titre est le **seul `h1`** de l'ecran ; les cartes en dessous portent des
 * `h2`. Le surtitre nomme la rubrique (« Pilotage ») et n'est pas un titre : il
 * precede le `h1` sans en faire partie. Il est ecrit en `accentPinkStrong`,
 * l'encre rose qui tient 4,5:1 sur le fond de page (`primary` ne tient que
 * 4,45:1 en clair).
 *
 * Les actions passent sous le titre sur un ecran etroit, au lieu de l'ecraser.
 */
export interface PageHeaderProps {
  readonly title: string;
  readonly eyebrow?: string;
  readonly subtitle?: ReactNode;
  /** Pastilles d'etat a cote du titre. */
  readonly badges?: ReactNode;
  readonly actions?: ReactNode;
}

export const PageHeader = ({ title, eyebrow, subtitle, badges, actions }: PageHeaderProps): ReactElement => (
  <Box
    component="header"
    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}
  >
    <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {eyebrow === undefined ? null : (
        <Typography
          component="p"
          sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'tokens.accentPinkStrong' }}
        >
          {eyebrow}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography component="h1" sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 800, lineHeight: { xs: '30px', sm: '34px' } }}>
          {title}
        </Typography>
        {badges}
      </Box>
      {subtitle === undefined ? null : (
        <Typography component="div" sx={{ fontSize: 15, lineHeight: '22px', color: 'tokens.textMuted', maxWidth: 720 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions === undefined ? null : <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>}
  </Box>
);
